import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchHealthSummary, type HealthGolongan, type HealthSummary } from '../lib/api';
import { isStaff, useAuth } from '../hooks/useAuth';
import { useBahasa } from '../i18n';

/**
 * Wall Status (§G14) — Daily Health Summary di kepala `/dashboard`.
 *
 * *"Kayak Daily Health Summary, tapi di halaman dashboard. Termasuk storage-nya
 * gimana. Berapa persen dsb."*
 *
 * **Satu bilah ±32px, dan itu angka yang harus dipertahankan.** Halaman ini
 * sudah pernah kehilangan 61px di puncaknya untuk dua kendali, dan bilah itu
 * dibongkar justru karena *"kamera jadi turun gara-gara block putih di atas"*
 * (§G9). Menaruh kartu ringkasan setinggi 120px di tempat yang sama adalah
 * kesalahan yang sama dengan alasan yang lebih baik.
 *
 * **Ikon + angka, namanya muncul saat di-hover** (1 September 2026, diminta
 * lapangan). Sampai revisi ini tiap butir menulis namanya sendiri —
 * "91 sehat · 3 waspada · 2 bermasalah · rekaman 99,4% · disk 68%" — dan pada
 * layar 1366px baris itu sudah menggulung ke samping sebelum sampai ke disk.
 * Yang dibaca orang setiap hari cuma **angkanya**; namanya dibaca sekali,
 * minggu pertama. Jadi nama pindah ke tooltip (dan ke `sr-only`, supaya pembaca
 * layar tidak ikut kehilangannya), angkanya tetap di layar, dan seluruh
 * ringkasan muat tanpa menggulung.
 *
 * **Hanya untuk admin & operator** (revisi 1 September 2026, diminta lapangan).
 * Sampai revisi ini viewer melihat versinya sendiri — angka yang dihitung dari
 * kameranya saja (§G14). Yang keliru bukan angkanya melainkan pertanyaannya:
 * "3 waspada, disk 68%, sisa ±9 hari" adalah kalimat yang menuntut seseorang
 * bertindak, dan viewer justru satu-satunya peran yang tidak punya satu pun
 * tombol untuk menindaklanjutinya. Ringkasan yang tidak bisa ditindaklanjuti
 * cuma jadi kecemasan yang tidak ada jalan keluarnya — dan lebih buruk lagi,
 * baris angka merah yang dilihat sepanjang hari tanpa pernah bisa berubah
 * mengajari orang mengabaikan warna merah.
 *
 * **Dua varian, satu isi:**
 *
 * - `bilah` (≥1024px) — baris tersendiri di kepala `/dashboard`, seperti semula.
 * - `ringkas` (<1024px) — menumpang di baris hamburger `AppHeader`. Di ponsel
 *   baris hamburger itu nyaris kosong (satu tombol, ±40px) sementara Wall Status
 *   membayar 32px penuh untuk barisnya sendiri; menumpang di sana mengembalikan
 *   32px itu ke gambar kamera (§G7 no.1) tanpa menghilangkan apa pun.
 *
 * Yang memilih varian adalah **pemanggilnya**, dan hanya satu yang pernah
 * dipasang pada satu waktu (`useLayarLebar`, bukan `lg:hidden`): komponen ini
 * memasang interval poll 60 detik, jadi dua salinan yang saling disembunyikan
 * CSS berarti dua permintaan per menit untuk satu bilah yang terlihat.
 */

const SIMPAN_CIUT = 'lensa.wall.collapsed';
/** Sama dengan panel kesehatan §D9.2: yang ditanya "masih sehat tidak", bukan "berapa milidetik". */
const POLL_MS = 60_000;

function bacaCiut(): boolean {
  try {
    return localStorage.getItem(SIMPAN_CIUT) === '1';
  } catch {
    return false;
  }
}

/**
 * Warna hanya muncul saat ada yang tidak beres (§G14).
 *
 * Bilah yang selalu berwarna berhenti berarti apa-apa dalam sehari — dan bilah
 * ini dilihat orang yang sama, setiap hari, sepanjang hari.
 */
function kelasAngka(golongan: HealthGolongan, n: number): string {
  if (n === 0) return 'text-muted';
  if (golongan === 'problem') return 'text-red-600';
  if (golongan === 'warning') return 'text-amber-600';
  return 'text-muted';
}

/**
 * Ikut gaya ikon sidebar (§G9): viewBox 24, stroke 1,75, ujung membulat.
 * Bobot stroke yang berbeda-beda antar ikon terbaca sebagai kesalahan cetak,
 * bukan sebagai variasi.
 */
const ikon = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  className: 'shrink-0',
};

/**
 * Satu ikon untuk satu arti, dan tidak ada dua yang mirip.
 *
 * Bilah ini dipindai dari sudut mata, bukan dibaca. Dua bentuk yang cuma beda
 * detail kecil — dua lingkaran dengan isi berbeda, misalnya — berarti orang
 * harus berhenti dan memastikan, dan begitu ia harus berhenti, ikon sudah kalah
 * dari kata yang digantikannya.
 */
const IKON = {
  /** Total kamera — badan kamera + lensanya, sama dengan ikon menu Cameras. */
  kamera: (
    <svg {...ikon}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h9A1.5 1.5 0 0 1 15 7.5v9A1.5 1.5 0 0 1 13.5 18h-9A1.5 1.5 0 0 1 3 16.5z" />
      <path d="m15 10.5 4.4-2.6a.75.75 0 0 1 1.1.65v6.9a.75.75 0 0 1-1.1.65L15 13.5z" />
    </svg>
  ),
  /** Sehat — centang. */
  sehat: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12.5 2.6 2.6L16 9.7" />
    </svg>
  ),
  /** Waspada — segitiga seru. */
  waspada: (
    <svg {...ikon}>
      <path d="M12 4.2 2.8 19.3h18.4z" />
      <path d="M12 10v3.6" />
      <path d="M12 16.6h.01" />
    </svg>
  ),
  /** Bermasalah — silang, bentuk yang paling jauh dari centang. */
  masalah: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="9" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </svg>
  ),
  /** Pemeliharaan — kunci pas, kosakata yang sama dengan label MAINTENANCE. */
  pemeliharaan: (
    <svg {...ikon}>
      <path d="M15.4 4.6a5 5 0 0 0-6.3 6.3L4 16l4 4 5.1-5.1a5 5 0 0 0 6.3-6.3l-3 3-2.1-2.1z" />
    </svg>
  ),
  /** Rekaman berjalan — titik rekam, satu-satunya bentuk penuh di bilah ini. */
  rekam: (
    <svg {...ikon}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  /** Disk — tumpukan piringan, bukan awan: yang dibicarakan volume di rak sendiri. */
  disk: (
    <svg {...ikon}>
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6" />
      <path d="M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </svg>
  ),
  /** Catatan insiden terbuka — bendera yang masih berdiri. */
  insiden: (
    <svg {...ikon}>
      <path d="M5 21V4" />
      <path d="M5 4.8h11.5l-2.2 3.6 2.2 3.6H5z" />
    </svg>
  ),
} as const;

/**
 * Satu butir ringkasan: ikon + angka di layar, kalimatnya di tooltip.
 *
 * `title` untuk tetikus, `sr-only` untuk pembaca layar. Keduanya perlu dan
 * tidak saling menggantikan — `title` tidak pernah sampai ke layar sentuh, dan
 * `aria-label` tidak pernah sampai ke mata.
 */
function Butir({
  ikon: gambar,
  teks,
  angka,
  warna,
  to,
}: {
  ikon: React.ReactNode;
  teks: string;
  angka: string;
  warna?: string;
  to?: string;
}) {
  const isi = (
    <>
      {gambar}
      <span className="tabular-nums">{angka}</span>
      <span className="sr-only">{teks}</span>
    </>
  );
  const kelas = `flex shrink-0 items-center gap-1 ${warna ?? 'text-muted'}`;

  // Tiap angka adalah tautan kalau memang ada yang bisa ditelusuri (§D12):
  // ringkasan yang tidak bisa ditelusuri hanya memindahkan pertanyaan.
  return to ? (
    <Link to={to} title={teks} className={`${kelas} rounded hover:underline`}>
      {isi}
    </Link>
  ) : (
    <span title={teks} className={kelas}>
      {isi}
    </span>
  );
}

export function WallStatus({ varian = 'bilah' }: { varian?: 'bilah' | 'ringkas' }) {
  const { t } = useBahasa();
  const { user } = useAuth();
  const [data, setData] = useState<HealthSummary | null>(null);
  const [ciut, setCiut] = useState(bacaCiut);
  const untukSaya = isStaff(user);

  const muat = useCallback(async () => {
    try {
      setData(await fetchHealthSummary());
    } catch {
      // Ringkasan yang gagal dimuat tidak boleh mengganggu dashboard: yang jadi
      // isi halaman ini gambar kameranya, dan bilah ini konteks.
    }
  }, []);

  useEffect(() => {
    // Viewer tidak pernah melihat bilah ini, jadi ia juga tidak perlu
    // memanggil endpoint-nya — poll 60 detik untuk sesuatu yang tidak dirender
    // adalah beban tetap tanpa satu pun piksel yang membayarnya.
    if (!untukSaya) return;
    void muat();
    const timer = window.setInterval(() => void muat(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [muat, untukSaya]);

  function simpanCiut(v: boolean) {
    setCiut(v);
    try {
      localStorage.setItem(SIMPAN_CIUT, v ? '1' : '0');
    } catch {
      // Tidak bisa diingat bukan alasan untuk tidak menciut.
    }
  }

  // `isStaff` sudah memperhitungkan User view (§F8.1): admin yang sedang melihat
  // sebagai user ikut kehilangan bilah ini, dan memang itu gunanya mode itu.
  if (!untukSaya || !data) return null;

  const ringkas = varian === 'ringkas';
  const adaMasalah = data.problem > 0;

  if (ciut) {
    return (
      <button
        type="button"
        onClick={() => simpanCiut(false)}
        aria-label={t('Show system status')}
        className={
          ringkas
            ? 'flex min-h-tap shrink-0 items-center gap-2 text-xs text-muted'
            : 'flex h-8 shrink-0 items-center gap-2 border-b border-line px-3 text-xs text-muted'
        }
      >
        <span
          aria-hidden
          className={`h-2 w-2 rounded-full ${adaMasalah ? 'bg-red-600' : 'bg-green-600'}`}
        />
        <span>{adaMasalah ? t('{n} need attention', { n: data.problem }) : t('All healthy')}</span>
      </button>
    );
  }

  const s = data.storage;
  const persenDisk =
    s?.quota
      ? s.quota.usedPercent
      : s && s.usedGb !== null && s.totalCapacityGb
        ? Number(((s.usedGb / s.totalCapacityGb) * 100).toFixed(1))
        : null;

  return (
    <div
      className={`flex items-center gap-x-3 overflow-x-auto text-xs whitespace-nowrap ${
        ringkas
          ? // Menumpang di baris hamburger: tanpa garis, tanpa tinggi sendiri,
            // dan `min-w-0` supaya yang menggulung adalah bilah ini — bukan
            // seluruh baris header yang mendorong tombol hamburger keluar layar.
            'min-w-0 flex-1'
          : 'h-8 shrink-0 border-b border-line px-3'
      }`}
    >
      <Butir
        ikon={IKON.kamera}
        teks={
          data.scope === 'assigned'
            ? `${t('{n} cameras', { n: data.total })} (${t('yours')})`
            : t('{n} cameras', { n: data.total })
        }
        angka={String(data.total)}
      />

      <Butir
        ikon={IKON.sehat}
        teks={t('{n} healthy', { n: data.healthy })}
        angka={String(data.healthy)}
        warna={kelasAngka('healthy', data.healthy)}
      />
      <Butir
        ikon={IKON.waspada}
        teks={t('{n} warning', { n: data.warning })}
        angka={String(data.warning)}
        warna={kelasAngka('warning', data.warning)}
        to="/logs?tab=aktivitas"
      />
      <Butir
        ikon={IKON.masalah}
        teks={t('{n} problem', { n: data.problem })}
        angka={String(data.problem)}
        warna={kelasAngka('problem', data.problem)}
        to="/logs?tab=aktivitas"
      />
      {data.maintenance > 0 && (
        <Butir
          ikon={IKON.pemeliharaan}
          teks={t('{n} in maintenance', { n: data.maintenance })}
          angka={String(data.maintenance)}
          warna="text-accent-text"
        />
      )}

      <Butir
        ikon={IKON.rekam}
        teks={t('recording {n}%', { n: data.uptimePct.toFixed(1) })}
        angka={`${data.uptimePct.toFixed(1)}%`}
      />

      {/* "68%" tidak memberi tahu apakah seseorang perlu bertindak minggu ini;
          "sisa ±9 hari" memberi tahu (§G14). Karena itu sisa hari **tidak** ikut
          pindah ke tooltip: ia justru angka yang paling perlu terlihat. */}
      {persenDisk !== null && (
        <Butir
          ikon={IKON.disk}
          teks={t('disk {n}%', { n: persenDisk })}
          angka={
            s?.daysUntilFull !== null && s?.daysUntilFull !== undefined
              ? `${persenDisk}% (${t('~{n} days left', { n: s.daysUntilFull })})`
              : `${persenDisk}%`
          }
        />
      )}

      {data.incidentsOpen > 0 && (
        <Butir
          ikon={IKON.insiden}
          teks={t('{n} open incidents', { n: data.incidentsOpen })}
          angka={String(data.incidentsOpen)}
          to="/logs?tab=aktivitas"
        />
      )}

      {/* Tombol ciut hanya di varian `bilah`. Di ponsel bilah ini sudah tidak
          memakan baris sendiri, jadi alasan menciutkannya hilang — sementara
          satu tombol lagi di baris yang juga memuat hamburger dan tombol keluar
          User view justru memakan lebar yang seharusnya jadi angka. Keadaan
          ciut tetap sama-sama dipakai kedua varian, dan bentuk ciutnya sendiri
          tetap bisa disentuh untuk membentang lagi. */}
      {!ringkas && (
        <button
          type="button"
          onClick={() => simpanCiut(true)}
          aria-label={t('Hide system status')}
          className="ml-auto shrink-0 px-1 text-muted"
        >
          ×
        </button>
      )}
    </div>
  );
}
